import DomainFactory from "../lib/DomainFactory"
import Base from "./Base"
import Composition from "./Composition"
import { ClusterElement } from "./Cluster"
import { baseAttribute, catalogAttribute, groupAttributes } from "./composition"
import { getModifier } from "../catalog/ArticleCollection";
import Toggles from "@domain/toggles/Toggles";
import TogglesV2 from "@domain/toggles/TogglesManager";
import Light from "./Light"
import Complement from "./Complement"
import { i18nLabelInterface } from "@domain/catalog/catalog"

const arrayIntersection = (array1, array2) => ({
	intersected: array1.filter(value => array2.includes(value)),
	nonIntersected: array1.filter(value => !array2.includes(value))
})
const objectArrayIntersection = (array1, array2) => {
	return array1.filter(elem1 => array2.some(elem2 => getModifier(elem1.value) == getModifier(elem2.value)))
}
const attributes = ['colors', 'temperatures', 'controls', 'extraColors', 'colorButtons', 'extraColorButtons', 'related']
const attributesV2 = ['temperaturesV2', 'controlsV2']
const objectAttributes = ['canopy']
export interface ComposableElement<T extends Base = Base> {
	element: T,
	options?: any
}

export class Group extends Base {
	virtual: boolean
	static className = "Group"
	type = 'Group';
	constructor(composableElements: ComposableElement[] | Group, parent?, virtual?, id?, attributes?: groupAttributes, data?: any) {
		super(parent, id, attributes, data)
		const group = composableElements as Group
		if (group.getId) {
			this.props = group.props
			this.virtual = group.virtual
		} else {
			this.props.elements = composableElements as ComposableElement[]
			this.virtual = virtual
		}

		if (!this.virtual) {
			this.getElements().forEach((element) => element.element.setParent(this.getInstance()))
		}

		if (Toggles.isEnabled("logDomainConstructor")) {
			console.log(`%c[Group]`, '🐦;background: lightblue; color: #444;padding: 3px; border-radius: 5px;', '🐦')
		}

		this.tagByCreationDate()
	}

	getNestedElements() {
		const result: any[] = []

		const traverseObject = (element, options) => {
			if (element.getClassName() === 'Cluster') {
				if (element.parent?.getClassName() !== 'Space' && element.parent?.getClassName() !== 'Composition')
					result.push(
						{
							element: element,
							options: options
						}
					)
				if (element.props && element.props.elements) {
					element.props.elements.forEach((el) => {
						traverseObject(el.element, el.options)
					})
				}
			} else if (element.getClassName() === 'Pendant') {
				result.push({
					element: element,
					options: options
				})
			}
		}

		traverseObject(this, null);
		return result
	}

	getElements() {
		return this.props.elements || []
	}

	getAllElements(): Array<Base> {
		const childs = this.getElements().map(element => element.element.getAllElements()).flat()
		const baseResult = super.getAllElements()
		return [...childs, ...baseResult]
	}

	getLights<T extends Light>(): ClusterElement<T>[] {
		return this.getElements().map((element: ComposableElement) => element.element.getLights()).flat()
	}

	getComplements<T extends Complement>(): ClusterElement<T>[] {
		return this.getElements().map((element: ComposableElement) => element.element.getComplements()).flat()
	}

	getCompositions(): Composition[] {
		return this.getElements().map((element: ComposableElement) => element.element.getCompositions()).flat()
	}

	getModels() {
		return this.getLights().map(({ element: light }) => light.getModel())
	}

	// TODO: Deprecate
	getModel() {
		return this.getModels()[0]
	}

	getElement(element: Base | ComposableElement | string): ComposableElement {
		var id;
		if (typeof element === 'string' || element instanceof String) {
			id = element
		} else {
			var subElement: Base = (element && (element as ComposableElement).element ? (element as ComposableElement).element : element) as Base
			id = subElement.getId()
		}

		return this.getElements().find(elem => elem.element.getId() === id)
	}

	getAttributeDescriptions(attribute: catalogAttribute): i18nLabelInterface {
		return {}
	}

	isVirtual() {
		return this.virtual
	}

	isEmpty() {
		return !this.getElements() || this.getElements().length == 0
	}

	validate() {
		super.validate();
		(this.getElements() as ComposableElement[]).forEach((element) => element.element.validate())
	}

	setAttributesByReference(reference: string) {
		this.getElements().forEach(element => element.element.setAttributesByReference(reference))
		return this
	}

	//own = apply the attribute to the group, no to the childrens of the group
	//strict = all elements of the group should accept the attribute. Apple the attribute only of all childrens are compatible with the change
	setAttribute(attribute: baseAttribute, value, own?) {
		if (own) {
			super.setAttribute(attribute, value)
		} else {
			(this.getElements() as ComposableElement[]).forEach((element) => element.element.setAttribute(attribute, value))
		}
		return this
	}

	deleteAttribute(attribute: baseAttribute, own?: boolean) {
		if (own) {
			super.deleteAttribute(attribute)
		} else {
			(this.getElements() as ComposableElement[]).forEach((element) => element.element.deleteAttribute(attribute))
		}
		return this
	}

	deleteInvalidAttributes(own?: boolean) {
		if (own) {
			super.deleteInvalidAttributes(own)
		} else {
			this.getElements().forEach((element: ComposableElement) => element.element.deleteInvalidAttributes())
		}
		return this
	}

	resetInvalidAttributes(own?: boolean) {
		if (own) {
			super.resetInvalidAttributes(own)
		} else {
			this.getElements().forEach((element: ComposableElement) => element.element.resetInvalidAttributes())
		}
		return this
	}

	setDeltaAttribute(attribute: baseAttribute, value, own?, strict?) {
		if (own) {
			super.setDeltaAttribute(attribute, value)
		} else {
			var failed = []
			try {
				(this.getElements() as ComposableElement[]).forEach((element) => element.element.requestSetAttribute(attribute, value + element.element.getAttribute(attribute)))
			} catch (e) {
				failed.push(e)
			}
			if (!failed.length) {
				(this.getElements() as ComposableElement[]).forEach((element) => element.element.setDeltaAttribute(attribute, value, own, strict))
			} else {
				throw "Bulk delta attribute failed because some elements failed to apply the attribute: " + JSON.stringify(failed)
			}
		}
		return this
	}

	addElement(param: ComposableElement | Base, prevId?: string) {
		let composableElement;
		var replaceId = prevId;
		//its a light, or a composable element?
		if (!param['element']) {
			replaceId = prevId ?? (param as Base).getId()
			//its a light, search for the composableElement associated with this light, to update its
			composableElement = this.getElements().find(elem => elem.element.getId() === replaceId)
			if (!composableElement)
				throw new Error('Element not found')
			composableElement.element = param as Base
		} else {
			composableElement = param;
		}

		if (!this.virtual) {
			composableElement.element.setParent(this.getInstance())
		}

		const newElements = this.getElements().filter(elem => elem.element.getId() !== replaceId && elem.element.getId() !== composableElement.element.getId()).concat(composableElement)

		if (Toggles.isEnabled('firstLightAfterDeleteMovement') && newElements[0]?.options?.position === null) {
			newElements[0].options.position = [0, 0, 0]
		}

		const group = new Group(newElements, this.getParent(), this.virtual, null, null, this.data)

		group.tagByCreationDate();

		return group;
	}

	getInfo() {
		const groupArticles = (array: any) => array.reduce((previous, actual) => {
			delete actual.wireLength
			const index = previous.findIndex(prev => {
				if (actual.reference) {
					return prev.reference === actual.reference
				} else {
					const attributesToMatch = ["model", "color", "control", "installation", "temperature", "pendantHeight"]
					return attributesToMatch.every(attribute => prev[attribute] === actual[attribute])
				}
			})
			index == -1 ? previous.push({ ...actual, count: actual.count ?? 1 }) : previous[index].count += actual.count ?? 1
			return previous
		}, [])

		return this.getElements().reduce((previous, actualComposable) => {
			const elementInfo = actualComposable?.element?.getInfo()
			let _articles = previous.articles

			// if (elementInfo.article) -> Son is a Light -> add data to array
			if (elementInfo.article)
				_articles.push(elementInfo.article)
			// if (!elementInfo.article) -> Son is a Group -> concat articles (want them alltogether)
			else
				_articles = _articles.concat(elementInfo.articles)

			const control = _articles.every(element => element.control === _articles[0]?.control) ? _articles[0]?.control : undefined
			const installation = _articles.every(element => element.installation === _articles[0]?.installation) ? _articles[0]?.installation : undefined

			const delivered_power = Math.max(previous.delivered_power, elementInfo?.delivered_power ?? 0)
			const consumed_power = previous.consumed_power += (elementInfo?.consumed_power ?? 0)

			const group = {
				height: Math.max(previous.height, isNaN(elementInfo?.height) ? -Infinity : elementInfo?.height),
				lumens: previous.lumens += (elementInfo?.lumens ?? 0),
				watts: previous.watts += (elementInfo?.watts ?? 0),
				delivered_power,
				consumed_power,
				netWeight: previous.netWeight += (elementInfo?.netWeight ?? 0),
				articles: groupArticles(_articles),
				control,
				installation,
				type: 'group'
			}

			return group
		}, { height: 0, lumens: 0, watts: 0, delivered_power: 0, consumed_power: 0, netWeight: 0, articles: [], control: true, installation: true })
	}

	getAbsolutePosition(child?: Base): number[] {
		const position = this.getParent() ? this.getParent().getAbsolutePosition(this) : [0, 0, 0]
		return child && this.getElement(child) && this.getElement(child).options && this.getElement(child).options.position ? this.getElement(child).options.position.map((x, i) => x + position[i]) : position
	}

	getBoundingBox(): { x: number; y: number; z: number; width: number; height: number; depth: number } {
		var origin;
		var positions: any = this.getElements().map(element => {
			const bb = element.element.getBoundingBox(true);
			origin = element.element.getOrigin()
			const euPos = element.element.getAbsolutePosition()

			return {
				maxX: euPos[0] + bb.x + (bb.width / 2),
				minX: euPos[0] + bb.x - (bb.width / 2),
				maxY: 0,// + (origin == 'center' ? bb.height / 2 : 0),
				minY: bb.height * -1,// - (origin == 'center' ? bb.height / 2 : bb.height),
				minZ: euPos[2] + bb.z - (bb.depth / 2),
				maxZ: euPos[2] + bb.z + (bb.depth / 2)
			}
		})

		positions = positions.reduce((total, pos) => {
			if (!total) return pos
			return {
				maxX: Math.max(total.maxX, pos.maxX),
				minX: Math.min(total.minX, pos.minX),
				maxY: Math.max(total.maxY, pos.maxY),
				minY: Math.min(total.minY, pos.minY),
				minZ: Math.min(total.minZ, pos.minZ),
				maxZ: Math.max(total.maxZ, pos.maxZ)
			}
		}, null)
		if (!positions) return { width: 0, height: 0, depth: 0, x: 0, y: 0, z: 0 }
		const clean = (num) => parseFloat(num.toFixed(3));

		const size = {
			width: clean(positions.maxX - positions.minX),
			height: clean(positions.maxY - positions.minY),
			depth: clean(positions.maxZ - positions.minZ),
		}

		return {
			...size,
			x: clean(positions.maxX - (size.width / 2)),
			y: clean(positions.minY + (origin == 'center' ? 0 : size.height)),
			z: clean(positions.maxZ - (size.depth / 2))
		}
	}

	/* NOTE -> This will need reimplementation when Clusters accept multiple Lights (Remote, Spider...) */
	moveElements(x, y, z) {
		this.getElements().forEach(element => element.element.moveElements(x, y, z))
		return this
	}

	rotate2DElements(rotationCenterX, rotationCenterZ, angle) {
		this.getElements().forEach(element => element.element.rotate2DElements(rotationCenterX, rotationCenterZ, angle))
		return this
	}

	remove(child: Base) {
		const childElement = this.getElement(child)
		if (!childElement)
			return this
		this.props.elements = this.getElements().filter((composableElement: ComposableElement) => composableElement.element.getId() !== childElement.element.getId())
		if (this.getElements().length == 0 && this.getParent()) {
			this.getParent().remove(this)
		}

		this.tagByCreationDate()


		return this
	}

	removeAll() {
		this.getElements().forEach((composableElement: ComposableElement) => composableElement.element.getParent().remove(composableElement.element))
	}

	toString() {
		return JSON.stringify(this.props, null, 4)
	}

	getInstallation(withModifier?: boolean) {
		return this.getAttribute('installation', withModifier)
	}

	setInstallation(installation: string) {
		return this.setAttribute('installation', installation, true)
	}

	deleteInstallation() {
		return this.deleteAttribute("installation", true)
	}

	tagByCreationDate() {
		const children = this.getElements()
		if (children) {
			return children
				.sort((elem1, elem2) => {
					return elem1.element.getElementCreatedTimeStamp() - elem2.element.getElementCreatedTimeStamp()
				})
				.forEach((elem, index) => {
					elem.element.setElementOrderValue(index + 1)
				})
		}
		return this
	}

	toJSON() {
		const elements = this.getElements().map(composableElement => ({
			element: composableElement.element.toJSON(),
			options: composableElement.options
		}))
		const baseProps = super.toJSON()
		return {
			...baseProps,
			props: {
				...baseProps.props,
				elements: elements,
			},
			createdAt: this.createdAt,
			virtual: this.virtual
		}
	}

	fromJSON(json, options?: any, instance?: Base) {
		return Group.fromJSON(json, options, instance)
	}

	getTechnicalInformation() {
		let allOptions = {
			finishes: [],
			controls: [],
			temperatures: [],
			installations: [],
			cri: [],
			materials: [],
			MOQ: 0
		}

		const addIfNotExist = (attribute: string, key: string) => sonValue => {
			const exists = allOptions[attribute].find(elem => elem[key] == sonValue[key])
			if (!exists)
				allOptions[attribute].push(sonValue)
		}

		this.getElements().forEach(({ element }, i) => {
			const technicalInformation = element.getTechnicalInformation()

			const attributesOptions = [
				{ attribute: "finishes", key: "modifier" },
				{ attribute: "controls", key: "modifier" },
				{ attribute: "temperatures", key: "modifier" },
				{ attribute: "installations", key: "modifier" },
				{ attribute: "cri", key: "cri" },
				{ attribute: "materials", key: "id" }
			]

			attributesOptions.forEach(({ attribute, key }) => {
				const sonAttributes = technicalInformation[attribute]
				sonAttributes.forEach(addIfNotExist(attribute, key))
			})

			if (technicalInformation.MOQ > allOptions.MOQ) {
				allOptions.MOQ = technicalInformation.MOQ
			}
		})

		return allOptions
	}

	getLightOptions() { //TODO: En vez de obtenerlas del primer hijo que las tenga, se deberian mergear todas las opciones que sean comunes entre todos los hijos
		/* TODO -> Hacer la intersección de los que no son Arrays (Canopy de momento) */
		let commonOptions: any = {}
		this.getElements().forEach(({ element }, i) => {
			const lightOptions = element.getLightOptions()

			attributes.forEach((attribute) => {
				if (i === 0) {
					commonOptions[attribute] = lightOptions[attribute] ?? []
					return
				}
				if (Array.isArray(commonOptions[attribute]) && Array.isArray(lightOptions[attribute])) {
					const { intersected: newArray, nonIntersected: deleted } = arrayIntersection(commonOptions[attribute], lightOptions[attribute])

					// When colors are deleted, remove buttonColors too
					if (attribute === 'colors' || attribute === 'extraColors' && deleted.length) {
						deleted.forEach(elem => {
							const index = commonOptions[attribute].indexOf(elem)
							if (attribute === 'colors')
								commonOptions.colorButtons.splice(index, 1)
							if (attribute === 'extraColors')
								commonOptions.extraColorButtons.splice(index, 1)
						})
					}
					commonOptions[attribute] = newArray
				}
			})
			attributesV2.forEach((attribute, index) => {
				if (i === 0) {
					commonOptions[attribute] = lightOptions[attribute] ?? []
					return
				}
				const newArray = objectArrayIntersection(commonOptions[attribute].options, lightOptions[attribute].options)
				commonOptions[attribute].options = newArray
			})
			objectAttributes.forEach((attribute) => {
				if (i === 0) {
					commonOptions[attribute] = lightOptions[attribute] ?? []
					return
				}
				const keys = Object.keys(lightOptions[attribute])
				const existingKeys = Object.keys(commonOptions[attribute])
				const { nonIntersected: deleted } = arrayIntersection(existingKeys, keys)
				deleted.forEach((del) => {
					delete commonOptions[attribute][del]
				})
			})
		})
		return commonOptions
	}

	getAttributeVariation(attribute, modifier?): any {
		throw new Error("This method is invalid for this element")
	}

	isAttributeCompatible(attribute, newValue): any {
		throw new Error("This method is invalid for this element")
	}

	getElementOptions(): any {
		throw new Error("This method is invalid for this element")
	}

	getOptionVariations(attribute): any {
		throw new Error("This method is invalid for this element")
	}

	getAttributeVariations(attribute): any {
		throw new Error("This method is invalid for this element")
	}

	attributeHasMultipleOptions(attribute): any {
		throw new Error("This method is invalid for this element")
	}

	getVariationsMOQ(attribute: catalogAttribute): number {
		return -1
	}

	getElementsByType(type): ComposableElement[] {
		let allElements = this.getElements();
		let elementFilterByType = allElements.filter(element => {
			if (element.element?.getProductTypology()?.toLowerCase() === type.toLowerCase()) {
				return true
			}
		})
		return elementFilterByType;
	}

	static fromJSON(json, options?: { domainFactory?: DomainFactory } & any, instance?: Base) {
		const elements = []
		for (const prop of json.props.elements) {
			elements.push({
				element: options.domainFactory ? options.domainFactory.getElement(prop.element) : prop,
				options: prop.options
			})
		}
		return super.fromJSON(json, options, instance ?? new Group(elements, null, json.virtual)) as Group
	}
}

export default Group